import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download, Filter, Search, AlertTriangle, RefreshCcw, Loader2,
  ArrowLeft, Edit2, Trash2, Plus, ExternalLink, ArrowUpRight, ArrowDownLeft, Clock
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { pagosTasasAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { Sparkles } from 'lucide-react';
import AnalisisIATasasBadge from '../components/AnalisisIATasasBadge';
import AnalisisIATasasModal from '../components/AnalisisIATasasModal';
import { obtenerToken } from '../lib/auth';
const IA_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TRAMITE_OPTS = [
  { value: 'demanda_propia', label: 'Demanda propia' },
  { value: 'recurso_reposicion', label: 'Recurso de Reposición' },
  { value: 'recurso_revision', label: 'Recurso de Revisión' },
  { value: 'recurso_apelacion', label: 'Recurso de Apelación' },
  { value: 'casacion', label: 'Casación' },
  { value: 'otro', label: 'Otro trámite' },
];
const METODO_OPTS = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'sede_electronica', label: 'Sede electrónica' },
];
const ESTADO_OPTS = [
  { value: 'pagada', label: 'Pagada' },
  { value: 'pendiente_devolucion', label: 'Pendiente de devolución' },
  { value: 'devuelta', label: 'Devuelta' },
  { value: 'no_recuperable', label: 'No recuperable' },
];

const estadoColor = (e) => ({
  pagada: 'bg-blue-100 text-blue-700',
  pendiente_devolucion: 'bg-amber-100 text-amber-700',
  devuelta: 'bg-green-100 text-green-700',
  no_recuperable: 'bg-gray-200 text-gray-700',
}[e] || 'bg-gray-100 text-gray-700');

const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

const EstadisticasPagoTasas = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totales, setTotales] = useState({ total_registros: 0, total_pagado: 0, total_devuelto: 0, pendiente_devolver: 0, pagos_vencidos: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({
    search: '', tipo: '', estado: '', tramite: '',
    fecha_desde: '', fecha_hasta: '', solo_vencidas: false,
    ia_estado: '', ia_revision: '',
  });
  // Edición in-place
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Creación desde estadísticas
  const [creating, setCreating] = useState(false);
  // Análisis IA
  const [iaModalPago, setIaModalPago] = useState(null);
  const [iaAnalizando, setIaAnalizando] = useState(false);
  const [iaJobId, setIaJobId] = useState(null);
  const [iaCancelando, setIaCancelando] = useState(false);
  const iaCancelRef = useRef({ cancelled: false });
  const [newForm, setNewForm] = useState(null);
  const [newExpSearch, setNewExpSearch] = useState('');
  const [newExpResults, setNewExpResults] = useState([]);
  const [newExpSelected, setNewExpSelected] = useState(null);
  const [newExpSearching, setNewExpSearching] = useState(false);
  const [newPagosOrigen, setNewPagosOrigen] = useState([]);

  // --- Datos agrupados para gráficos ---
  const chartData = useMemo(() => {
    const porTramite = {};
    TRAMITE_OPTS.forEach(t => { porTramite[t.value] = { name: t.label, pagado: 0, devuelto: 0, pendiente: 0 }; });
    items.forEach(p => {
      const key = p.tramite || 'otro';
      if (!porTramite[key]) porTramite[key] = { name: p.tramite_display || key, pagado: 0, devuelto: 0, pendiente: 0 };
      if (p.tipo === 'pago') {
        porTramite[key].pagado += p.importe || 0;
        if (p.estado === 'pendiente_devolucion') porTramite[key].pendiente += p.importe || 0;
      } else if (p.tipo === 'devolucion') {
        porTramite[key].devuelto += p.importe || 0;
      }
    });
    const tramiteArr = Object.values(porTramite).filter(r => r.pagado > 0 || r.devuelto > 0);
    tramiteArr.forEach(r => {
      r.porc_devuelto = r.pagado > 0 ? Math.round((r.devuelto / r.pagado) * 100) : 0;
    });
    return tramiteArr;
  }, [items]);

  const pieData = chartData.map(r => ({ name: r.name, value: r.pagado }));
  const PIE_COLORS = ['#2563eb', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== false && v !== null && v !== undefined) params[k] = v;
      });
      const res = await pagosTasasAPI.listGlobal(params);
      setItems(res.data.items || []);
      setTotales(res.data.totales || {});
      setSelected(new Set());
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const body = selected.size > 0
        ? { ids: Array.from(selected) }
        : {
          fecha_desde: filters.fecha_desde || undefined,
          fecha_hasta: filters.fecha_hasta || undefined,
          estado: filters.estado || undefined,
          tipo: filters.tipo || undefined,
          tramite: filters.tramite || undefined,
        };
      const res = await pagosTasasAPI.exportXlsx(body);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pagos_tasas_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Exportado', description: `Descargados ${selected.size > 0 ? selected.size : items.length} registros` });
    } catch {
      toast({ title: 'Error', description: 'No se pudo generar el Excel', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      tipo: item.tipo, tramite: item.tramite, metodo: item.metodo || '',
      estado: item.estado, concepto: item.concepto || '',
      fecha: toDateInput(item.fecha), importe: item.importe ?? '',
      pago_origen_id: item.pago_origen_id ? String(item.pago_origen_id) : '',
      notas: item.notas || '',
      fecha_pagada: toDateInput(item.fecha_pagada),
      fecha_pendiente_devolucion: toDateInput(item.fecha_pendiente_devolucion),
      fecha_devuelta: toDateInput(item.fecha_devuelta),
      fecha_no_recuperable: toDateInput(item.fecha_no_recuperable),
    });
  };

  const handleSave = async () => {
    if (!form.importe || parseFloat(form.importe) <= 0) {
      toast({ title: 'Error', description: 'Importe inválido', variant: 'destructive' }); return;
    }
    if (form.tipo === 'devolucion' && !form.pago_origen_id) {
      toast({ title: 'Error', description: 'Devolución requiere pago original vinculado', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      const toISO = (d) => d ? new Date(d).toISOString() : null;
      const payload = {
        tipo: form.tipo, tramite: form.tramite, metodo: form.metodo || null,
        estado: form.estado, concepto: form.concepto || null,
        fecha: toISO(form.fecha),
        importe: parseFloat(form.importe),
        pago_origen_id: form.pago_origen_id ? parseInt(form.pago_origen_id, 10) : null,
        notas: form.notas || null,
        fecha_pagada: toISO(form.fecha_pagada),
        fecha_pendiente_devolucion: toISO(form.fecha_pendiente_devolucion),
        fecha_devuelta: toISO(form.fecha_devuelta),
        fecha_no_recuperable: toISO(form.fecha_no_recuperable),
      };
      await pagosTasasAPI.update(editing.id, payload);
      toast({ title: 'Actualizado' });
      setEditing(null);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo guardar';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await pagosTasasAPI.delete(deleteTarget.id);
      toast({ title: 'Eliminado' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  // --- CREACIÓN ---
  const openCreate = () => {
    setNewForm({
      tipo: 'pago', tramite: 'demanda_propia', metodo: '',
      estado: 'pagada', concepto: '',
      fecha: new Date().toISOString().slice(0, 10),
      importe: '', pago_origen_id: '', notas: '',
    });
    setNewExpSearch('');
    setNewExpResults([]);
    setNewExpSelected(null);
    setNewPagosOrigen([]);
    setCreating(true);
  };

  const searchNewExp = async (query) => {
    setNewExpSearch(query);
    if (!query || query.length < 2) { setNewExpResults([]); return; }
    setNewExpSearching(true);
    try {
      const res = await expedientesAPI.getAll({ search: query, page_size: 20 });
      setNewExpResults(res.data.data || res.data || []);
    } catch {
      setNewExpResults([]);
    }
    setNewExpSearching(false);
  };

  const selectNewExp = async (exp) => {
    setNewExpSelected(exp);
    setNewExpSearch('');
    setNewExpResults([]);
    // Cargar pagos origen del expediente (para posibles devoluciones)
    try {
      const res = await pagosTasasAPI.listByExpediente(exp.id);
      setNewPagosOrigen((res.data || []).filter(p => p.tipo === 'pago'));
    } catch {
      setNewPagosOrigen([]);
    }
  };

  const handleCreate = async () => {
    if (!newExpSelected) {
      toast({ title: 'Error', description: 'Selecciona un expediente', variant: 'destructive' }); return;
    }
    if (!newForm.importe || parseFloat(newForm.importe) <= 0) {
      toast({ title: 'Error', description: 'Importe inválido', variant: 'destructive' }); return;
    }
    if (newForm.tipo === 'devolucion' && !newForm.pago_origen_id) {
      toast({ title: 'Error', description: 'Devolución requiere pago original vinculado', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      const payload = {
        tipo: newForm.tipo, tramite: newForm.tramite, metodo: newForm.metodo || null,
        estado: newForm.estado, concepto: newForm.concepto || null,
        fecha: newForm.fecha ? new Date(newForm.fecha).toISOString() : null,
        importe: parseFloat(newForm.importe),
        pago_origen_id: newForm.pago_origen_id ? parseInt(newForm.pago_origen_id, 10) : null,
        notas: newForm.notas || null,
      };
      await pagosTasasAPI.createForExpediente(newExpSelected.id, payload);
      toast({ title: 'Creado', description: 'Pago registrado y actuación generada en el expediente' });
      setCreating(false);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo crear', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ── Análisis IA ──────────────────────────────────────────────────────
  const handleAnalizarIALote = async () => {
    // 1) Si hay filas seleccionadas, analiza SOLO esas. Si no, pregunta
    //    antes por los pendientes del filtro actual.
    const seleccion = Array.from(selected);
    const idsSeleccionados = seleccion.length > 0
      ? items.filter(p => selected.has(p.id) && p.tipo === 'pago'
                          && (p.estado === 'pagada' || p.estado === 'pendiente_devolucion'))
             .map(p => p.id)
      : null;

    const totalPendientes = items.filter(p => p.tipo === 'pago'
      && (p.estado === 'pagada' || p.estado === 'pendiente_devolucion')).length;

    if (idsSeleccionados && idsSeleccionados.length === 0) {
      toast({ title: 'Selección inválida',
              description: 'Los registros marcados no son pagos pendientes de resolución.',
              variant: 'destructive' });
      return;
    }

    const cuantos = idsSeleccionados ? idsSeleccionados.length : totalPendientes;
    if (cuantos === 0) {
      toast({ title: 'Nada que analizar', description: 'No hay pagos pendientes de resolución.' });
      return;
    }
    const mensaje = idsSeleccionados
      ? `Se analizarán con IA ${cuantos} pago(s) SELECCIONADO(S). ¿Continuar?`
      : `No has seleccionado ninguna fila. Se analizarán con IA TODOS los ${cuantos} pagos pendientes del filtro actual. ¿Continuar?`;
    if (!window.confirm(mensaje)) return;

    iaCancelRef.current.cancelled = false;
    setIaCancelando(false);
    setIaAnalizando(true);
    let currentJobId = null;
    try {
      const token = obtenerToken();
      const body = idsSeleccionados ? { ids: idsSeleccionados } : {};
      // 1) Lanzar el job (responde inmediatamente)
      const r = await axios.post(`${IA_BACKEND_URL}/api/pagos-tasas/analizar-ia-lote`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      const { job_id, total, status } = r.data || {};
      if (!job_id || status === 'done') {
        toast({ title: 'Sin trabajo', description: 'No había pagos pendientes.' });
        await load();
        return;
      }
      currentJobId = job_id;
      setIaJobId(job_id);
      toast({ title: 'Análisis IA iniciado', description: `Procesando ${total} pago(s) en segundo plano…` });

      // 2) Polling cada 3s hasta que termine o se cancele
      const pollUrl = `${IA_BACKEND_URL}/api/pagos-tasas/analizar-ia-lote/${job_id}/estado`;
      let done = false;
      while (!done) {
        await new Promise(res => setTimeout(res, 3000));
        if (iaCancelRef.current.cancelled) break;
        const s = await axios.get(pollUrl, { headers: { Authorization: `Bearer ${token}` } });
        const d = s.data || {};
        if (d.status === 'done' || d.status === 'cancelled') {
          done = true;
          const resumen = Object.entries(d.por_estado || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
          toast({
            title: d.status === 'cancelled'
              ? `Análisis IA cancelado · ${d.ok || 0}/${d.total || 0} procesados`
              : `Análisis IA completado · ${d.ok || 0}/${d.total || 0} OK`,
            description: resumen || 'Sin propuestas',
          });
          await load();
        } else {
          if (d.procesados % 5 === 0 || d.procesados === d.total - 1) {
            console.log(`[IA lote] ${d.procesados}/${d.total}`);
          }
        }
      }
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setIaAnalizando(false);
      setIaJobId(null);
      setIaCancelando(false);
    }
  };

  const handleCancelarIALote = async () => {
    if (!iaJobId) return;
    if (!window.confirm('¿Cancelar el análisis IA en curso? Los pagos ya procesados se conservarán.')) return;
    setIaCancelando(true);
    try {
      const token = obtenerToken();
      await axios.delete(`${IA_BACKEND_URL}/api/pagos-tasas/analizar-ia-lote/${iaJobId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      iaCancelRef.current.cancelled = true;
      toast({ title: 'Cancelación solicitada',
              description: 'El worker parará tras acabar los análisis en vuelo.' });
    } catch (e) {
      toast({ title: 'Error al cancelar',
              description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      setIaCancelando(false);
    }
  };

  const refreshPagoIA = async (pagoId) => {
    await load();
    if (iaModalPago?.id === pagoId) {
      const fresco = items.find(x => x.id === pagoId);
      if (fresco) setIaModalPago(fresco);
    }
  };

  return (
    <div className="space-y-5" data-testid="estadisticas-pago-tasas">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/expedientes')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <h1 className="text-2xl font-semibold text-gray-800">Estadísticas — Pago de Tasas</h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Espacio reservado — los botones Nuevo pago y Exportar están junto a Aplicar/Refrescar */}
        </div>
      </div>

      {/* Totales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-lg border bg-gray-50 p-3">
          <div className="text-xs text-gray-600 font-medium">Total registros</div>
          <div className="text-xl font-bold text-gray-900">{totales.total_registros || 0}</div>
        </div>
        <div className="rounded-lg border bg-blue-50 p-3">
          <div className="text-xs text-blue-700 font-medium">Total pagado</div>
          <div className="text-xl font-bold text-blue-900">{(totales.total_pagado || 0).toFixed(2)} €</div>
        </div>
        <div className="rounded-lg border bg-green-50 p-3">
          <div className="text-xs text-green-700 font-medium">Total devuelto</div>
          <div className="text-xl font-bold text-green-900">{(totales.total_devuelto || 0).toFixed(2)} €</div>
        </div>
        <div className="rounded-lg border bg-amber-50 p-3">
          <div className="text-xs text-amber-700 font-medium">Pendiente devolver</div>
          <div className="text-xl font-bold text-amber-900">{(totales.pendiente_devolver || 0).toFixed(2)} €</div>
        </div>
        <div className="rounded-lg border bg-rose-50 p-3">
          <div className="text-xs text-rose-700 font-medium flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Vencidas &gt;6 meses
          </div>
          <div className="text-xl font-bold text-rose-900">{totales.pagos_vencidos || 0}</div>
        </div>
      </div>

      {/* Gráficos */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Distribución de tasas pagadas por trámite</h3>
                <span className="text-xs text-gray-500">Total: {(totales.total_pagado || 0).toFixed(2)} €</span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                       outerRadius={90} innerRadius={45} paddingAngle={2}
                       label={(entry) => `${entry.name}: ${entry.value.toFixed(0)}€`}>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => `${Number(v).toFixed(2)} €`} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">% recuperación (devuelto/pagado) por trámite</h3>
                <span className="text-xs text-gray-500">
                  Global: {totales.total_pagado > 0 ? Math.round((totales.total_devuelto / totales.total_pagado) * 100) : 0}%
                </span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={60} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} unit="€" />
                  <Tooltip
                    formatter={(v, n) => n === 'porc_devuelto' ? [`${v}%`, '% recuperación'] : [`${Number(v).toFixed(2)} €`, n === 'pagado' ? 'Pagado' : n === 'devuelto' ? 'Devuelto' : 'Pendiente']}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="pagado" fill="#2563eb" name="Pagado" />
                  <Bar dataKey="devuelto" fill="#10b981" name="Devuelto" />
                  <Bar dataKey="pendiente" fill="#f59e0b" name="Pendiente" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {chartData.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                    <span className="truncate mr-2" title={r.name}>{r.name}</span>
                    <Badge className={r.porc_devuelto >= 50 ? 'bg-green-100 text-green-700' : r.porc_devuelto > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-700'}>
                      {r.porc_devuelto}%
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Filter className="w-4 h-4" /> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Buscar</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                <Input className="pl-8" placeholder="Expediente, cliente, concepto..."
                       value={filters.search}
                       onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={filters.tipo || 'all'} onValueChange={(v) => setFilters({ ...filters, tipo: v === 'all' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="devolucion">Devolución</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <Select value={filters.estado || 'all'} onValueChange={(v) => setFilters({ ...filters, estado: v === 'all' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {ESTADO_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Trámite</Label>
              <Select value={filters.tramite || 'all'} onValueChange={(v) => setFilters({ ...filters, tramite: v === 'all' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {TRAMITE_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fecha desde</Label>
              <Input type="date" value={filters.fecha_desde}
                     onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Fecha hasta</Label>
              <Input type="date" value={filters.fecha_hasta}
                     onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })} />
            </div>
            <div className="flex items-end gap-2">
              <Checkbox id="vencidas" checked={filters.solo_vencidas}
                        onCheckedChange={(v) => setFilters({ ...filters, solo_vencidas: !!v })}
                        data-testid="checkbox-solo-vencidas" />
              <Label htmlFor="vencidas" className="cursor-pointer text-sm">Solo vencidas (&gt;6m)</Label>
            </div>
            <div>
              <Label className="text-xs">Estado IA</Label>
              <Select value={filters.ia_estado || 'all'} onValueChange={(v) => setFilters({ ...filters, ia_estado: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="filter-ia-estado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="con_analisis">Con análisis IA</SelectItem>
                  <SelectItem value="sin_analizar">Sin análisis IA</SelectItem>
                  <SelectItem value="SE_PUEDE_RECLAMAR">Se puede reclamar</SelectItem>
                  <SelectItem value="POSIBLE_NO_RECUPERABLE">Posible no recuperable</SelectItem>
                  <SelectItem value="PENDIENTE_RESOLUCION">Pendiente resolución</SelectItem>
                  <SelectItem value="NO_APLICA">No aplica</SelectItem>
                  <SelectItem value="REVISAR_MANUAL">Revisar manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Revisión IA</Label>
              <Select value={filters.ia_revision || 'all'} onValueChange={(v) => setFilters({ ...filters, ia_revision: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="filter-ia-revision"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente_revision">Pendiente de revisar</SelectItem>
                  <SelectItem value="revisado">Ya revisados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-end gap-2 flex-wrap md:col-span-3">
              <Button variant="outline" size="sm" onClick={load}>
                <RefreshCcw className="w-4 h-4 mr-1" /> Aplicar / Refrescar
              </Button>
              <Button size="sm" onClick={openCreate} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-new-from-stats">
                <Plus className="w-4 h-4 mr-1" /> Nuevo pago
              </Button>
              <Button size="sm" onClick={handleExport} disabled={exporting || items.length === 0} className="bg-green-600 hover:bg-green-700" data-testid="btn-export-xlsx">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                {selected.size > 0 ? `Exportar ${selected.size} seleccionados` : 'Exportar todos'}
              </Button>
              <Button size="sm" onClick={handleAnalizarIALote} disabled={iaAnalizando}
                      className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-ia-analizar-lote"
                      title="Si hay filas marcadas, analiza solo esas. Si no, pedirá confirmación para analizar todas las pendientes del filtro.">
                {iaAnalizando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {iaAnalizando
                  ? 'Analizando...'
                  : (selected.size > 0 ? `Analizar IA (${selected.size} sel.)` : 'Analizar con IA')}
              </Button>
              {iaAnalizando && iaJobId && (
                <Button size="sm" variant="destructive" onClick={handleCancelarIALote}
                        disabled={iaCancelando} data-testid="btn-ia-cancelar-lote">
                  {iaCancelando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {iaCancelando ? 'Cancelando...' : 'Cancelar IA'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-gray-500">No hay registros que coincidan con los filtros.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left sticky top-0">
                  <tr>
                    <th className="px-3 py-2">
                      <Checkbox checked={selected.size === items.length && items.length > 0}
                                onCheckedChange={toggleSelectAll}
                                data-testid="checkbox-select-all" />
                    </th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Expediente</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Cliente</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Fecha</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Tipo</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Trámite</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Concepto</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 text-right">Importe</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Estado</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Propuesta IA</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className={`border-t hover:bg-gray-50 ${p.alerta_vencida ? 'bg-rose-50/60' : ''}`} data-testid={`row-${p.id}`}>
                      <td className="px-3 py-2">
                        <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSelect(p.id)}
                                  data-testid={`checkbox-row-${p.id}`} />
                      </td>
                      <td className="px-3 py-2">
                        <a href={`/expedientes/${p.expediente_id}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="text-blue-600 hover:underline inline-flex items-center gap-1"
                           data-testid={`link-expediente-${p.expediente_id}`}>
                          {p.expediente?.numero_expediente || `#${p.expediente_id}`}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        <div className="text-xs text-gray-500 max-w-xs truncate" title={p.expediente?.titulo}>
                          {p.expediente?.titulo}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{p.cliente_nombre || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{p.fecha ? new Date(p.fecha).toLocaleDateString('es-ES') : '—'}</td>
                      <td className="px-3 py-2">
                        {p.tipo === 'pago' ? (
                          <span className="inline-flex items-center gap-1 text-blue-700"><ArrowUpRight className="w-4 h-4" />Pago</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-green-700"><ArrowDownLeft className="w-4 h-4" />Devolución</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{p.tramite_display}</td>
                      <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={p.concepto}>{p.concepto || '—'}</td>
                      <td className="px-3 py-2 text-right font-medium">{(p.importe || 0).toFixed(2)} €</td>
                      <td className="px-3 py-2">
                        <Badge className={estadoColor(p.estado)}>{p.estado_display}</Badge>
                        {p.alerta_vencida && (
                          <div className="text-xs text-rose-600 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" /> {p.dias_pendientes}d
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <AnalisisIATasasBadge
                          estado={p.ia_estado_propuesto}
                          confianza={p.ia_confianza}
                          revisado={!!p.ia_revisado_at}
                          onClick={() => setIaModalPago(p)}
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`btn-edit-${p.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} data-testid={`btn-delete-${p.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DIALOG EDIT */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar registro #{editing?.id}</DialogTitle>
            <DialogDescription>
              Expediente {editing?.expediente?.numero_expediente} — {editing?.cliente_nombre}
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <div className="mt-1 flex items-center gap-2" data-testid="edit-tipo-readonly">
                    {form.tipo === 'devolucion' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">Devolución</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">Pago</span>
                    )}
                    <span className="text-xs text-gray-500">
                      {form.tipo === 'devolucion' && editing?.pago_origen_id
                        ? `Registro de devolución vinculado al pago #${editing.pago_origen_id}.`
                        : 'Si se te ha devuelto, usa el botón "Registrar devolución" del listado — no cambies este registro.'}
                    </span>
                  </div>
                </div>
                <div>
                  <Label>Trámite</Label>
                  <Select value={form.tramite} onValueChange={(v) => setForm({ ...form, tramite: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRAMITE_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Concepto</Label>
                <Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Fecha</Label>
                  <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
                </div>
                <div>
                  <Label>Importe (€)</Label>
                  <Input type="number" step="0.01" value={form.importe} onChange={(e) => setForm({ ...form, importe: e.target.value })} />
                </div>
                <div>
                  <Label>Método</Label>
                  <Select value={form.metodo || 'none'} onValueChange={(v) => setForm({ ...form, metodo: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      {METODO_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTADO_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="border rounded-md p-3 bg-gray-50">
                <div className="text-xs font-semibold text-gray-700 mb-2">Fechas de cambio de estado (timings)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Fecha pagada</Label>
                    <Input type="date" value={form.fecha_pagada || ''} onChange={(e) => setForm({ ...form, fecha_pagada: e.target.value })} /></div>
                  <div><Label className="text-xs">Fecha pendiente devolución</Label>
                    <Input type="date" value={form.fecha_pendiente_devolucion || ''} onChange={(e) => setForm({ ...form, fecha_pendiente_devolucion: e.target.value })} /></div>
                  <div><Label className="text-xs">Fecha devuelta</Label>
                    <Input type="date" value={form.fecha_devuelta || ''} onChange={(e) => setForm({ ...form, fecha_devuelta: e.target.value })} /></div>
                  <div><Label className="text-xs">Fecha no recuperable</Label>
                    <Input type="date" value={form.fecha_no_recuperable || ''} onChange={(e) => setForm({ ...form, fecha_no_recuperable: e.target.value })} /></div>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Al cambiar el "Estado" se rellena automáticamente la fecha si está vacía. Puedes corregir cualquier fecha manualmente.</p>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700" disabled={saving} data-testid="btn-save-edit">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG CREAR (desde estadisticas, con buscador de expediente) */}
      <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nuevo pago de tasa</DialogTitle>
            <DialogDescription>
              Se asociará al expediente seleccionado y se creará automáticamente la actuación correspondiente.
            </DialogDescription>
          </DialogHeader>
          {newForm && (
            <div className="space-y-4">
              {/* Selector expediente */}
              <div>
                <Label>Expediente *</Label>
                {!newExpSelected ? (
                  <>
                    <div className="relative mt-1">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Escribe número de expediente o cliente..."
                        value={newExpSearch}
                        onChange={(e) => searchNewExp(e.target.value)}
                        className="pl-8"
                        data-testid="new-exp-search"
                      />
                    </div>
                    {newExpSearching && <div className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Buscando...</div>}
                    {newExpResults.length > 0 && (
                      <div className="border rounded-md mt-1 max-h-40 overflow-y-auto bg-white">
                        {newExpResults.map(exp => (
                          <div key={exp.id}
                               className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                               onClick={() => selectNewExp(exp)}
                               data-testid={`new-exp-opt-${exp.id}`}>
                            <span className="font-medium">{exp.numero_expediente}</span>
                            <span className="text-gray-500"> - {exp.titulo}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-between bg-blue-50 p-2 rounded-md mt-1">
                    <div className="text-sm">
                      <span className="font-medium">{newExpSelected.numero_expediente}</span>
                      <span className="text-gray-600"> - {newExpSelected.titulo}</span>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setNewExpSelected(null); setNewPagosOrigen([]); }}>
                      Cambiar
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo *</Label>
                  <Select value={newForm.tipo} onValueChange={(v) => setNewForm({ ...newForm, tipo: v, pago_origen_id: v === 'pago' ? '' : newForm.pago_origen_id })}>
                    <SelectTrigger data-testid="new-select-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pago">Pago</SelectItem>
                      <SelectItem value="devolucion">Devolución</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Trámite *</Label>
                  <Select value={newForm.tramite} onValueChange={(v) => setNewForm({ ...newForm, tramite: v })}>
                    <SelectTrigger data-testid="new-select-tramite"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRAMITE_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Concepto</Label>
                <Input value={newForm.concepto} onChange={(e) => setNewForm({ ...newForm, concepto: e.target.value })}
                       placeholder="Descripción del pago..." />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Fecha *</Label>
                  <Input type="date" value={newForm.fecha} onChange={(e) => setNewForm({ ...newForm, fecha: e.target.value })} />
                </div>
                <div>
                  <Label>Importe (€) *</Label>
                  <Input type="number" step="0.01" min="0" value={newForm.importe}
                         onChange={(e) => setNewForm({ ...newForm, importe: e.target.value })}
                         placeholder="0.00" data-testid="new-input-importe" />
                </div>
                <div>
                  <Label>Método</Label>
                  <Select value={newForm.metodo || 'none'} onValueChange={(v) => setNewForm({ ...newForm, metodo: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      {METODO_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estado *</Label>
                  <Select value={newForm.estado} onValueChange={(v) => setNewForm({ ...newForm, estado: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ESTADO_OPTS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {newForm.tipo === 'devolucion' && newExpSelected && (
                  <div>
                    <Label>Pago original vinculado *</Label>
                    <Select value={newForm.pago_origen_id || 'none'}
                            onValueChange={(v) => setNewForm({ ...newForm, pago_origen_id: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="new-select-pago-origen"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Ninguno —</SelectItem>
                        {newPagosOrigen.map(p => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            #{p.id} · {p.tramite_display} · {(p.importe || 0).toFixed(2)}€ · {p.fecha ? new Date(p.fecha).toLocaleDateString('es-ES') : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <Label>Notas</Label>
                <Textarea rows={2} value={newForm.notas} onChange={(e) => setNewForm({ ...newForm, notas: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={handleCreate} className="bg-cyan-600 hover:bg-cyan-700" disabled={saving} data-testid="btn-save-new">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Crear pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Expediente {deleteTarget?.expediente?.numero_expediente} — {deleteTarget?.tipo_display} de {(deleteTarget?.importe || 0).toFixed(2)}€.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal propuesta IA */}
      <AnalisisIATasasModal
        pago={iaModalPago}
        open={!!iaModalPago}
        onOpenChange={(v) => !v && setIaModalPago(null)}
        onAfterReject={refreshPagoIA}
        onAnalizarAhora={refreshPagoIA}
      />
    </div>
  );
};

export default EstadisticasPagoTasas;
